/**
 * Bedridden Patients API Service
 *
 * SWR hooks for bedridden patients data
 */

import useSWR from 'swr'
import { apiClient } from '../../shared/api/client'

/**
 * Statistics Response Type
 */
interface BedriddenStatsResponse {
  total: number
  by_type: {
    home: number
    bed: number
  }
  by_severity: {
    high: number
    medium: number
    low: number
  }
  by_district?: Array<{
    district_id: number
    district_name: string
    count: number
  }>
}

/**
 * Fetch bedridden patients statistics
 *
 * GET /patients/bedridden/stats
 */
export function useBedriddenStats(params?: { district_id?: number; year?: number }) {
  const queryParams = new URLSearchParams()
  if (params?.district_id) {
    queryParams.append('district_id', params.district_id.toString())
  }
  if (params?.year) {
    queryParams.append('year', params.year.toString())
  }

  const url = `/patients/bedridden/stats${queryParams.toString() ? `?${queryParams.toString()}` : ''}`

  const { data, error, isLoading } = useSWR<BedriddenStatsResponse>(
    url,
    apiClient.fetcher,
    {
      revalidateOnFocus: false,
      dedupingInterval: 10000, // 10 seconds
    }
  )

  return {
    data,
    isLoading,
    error,
  }
}

/**
 * Fetch bedridden patients markers for map
 *
 * GET /patients/bedridden/markers
 */
export function useBedriddenMarkers(params?: { district_id?: number }) {
  const queryParams = new URLSearchParams()
  if (params?.district_id) {
    queryParams.append('district_id', params.district_id.toString())
  }

  const url = `/patients/bedridden/markers${queryParams.toString() ? `?${queryParams.toString()}` : ''}`

  const { data, error, isLoading } = useSWR(url, apiClient.fetcher, {
    revalidateOnFocus: false,
    dedupingInterval: 30000, // 30 seconds
  })

  return {
    data,
    isLoading,
    error,
  }
}

/**
 * District Map Data for Polygon Map
 */
export interface DistrictMapData {
  district_id: number
  district_name_th: string
  district_name_en: string
  count: number
  concentration_level: 'low' | 'medium' | 'high'
  by_type?: { home: number; bed: number }
  by_severity?: { high: number; medium: number; low: number }
}

/**
 * Fetch bedridden patients data formatted for polygon map
 *
 * GET /patients/bedridden/stats → Transform to district map format
 */
export function useBedriddenDistrictMap(params?: { district_id?: number; year?: number }) {
  const statsQuery = useBedriddenStats(params)

  // Transform stats to district map format
  const districtMapData: DistrictMapData[] = statsQuery.data?.by_district?.map((d: any) => ({
    district_id: d.id,
    district_name_th: d.name_th,
    district_name_en: d.name_en,
    count: d.count,
    concentration_level: (d.count >= 50 ? 'high' : d.count >= 20 ? 'medium' : 'low') as 'low' | 'medium' | 'high',
  })) || []

  return {
    data: districtMapData,
    isLoading: statsQuery.isLoading,
    error: statsQuery.error,
  }
}

/**
 * Top Diseases Response Type
 */
export interface TopDisease {
  id: number
  name: string
  nameEn: string
  count: number
  percentage: number
}

/**
 * Fetch top chronic diseases
 *
 * GET /patients/bedridden/top-diseases
 */
export function useBedriddenTopDiseases(params?: { district_id?: number; year?: number; limit?: number }) {
  const queryParams = new URLSearchParams()
  if (params?.district_id) {
    queryParams.append('district_id', params.district_id.toString())
  }
  if (params?.year) {
    queryParams.append('year', params.year.toString())
  }
  if (params?.limit) {
    queryParams.append('limit', params.limit.toString())
  }

  const url = `/patients/bedridden/top-diseases${queryParams.toString() ? `?${queryParams.toString()}` : ''}`

  const { data, error, isLoading } = useSWR<TopDisease[]>(url, apiClient.fetcher, {
    revalidateOnFocus: false,
    dedupingInterval: 30000, // 30 seconds
  })

  return {
    data: data || [],
    isLoading,
    error,
  }
}

/**
 * Monthly Trends Response Type
 */
export interface MonthlyTrend {
  month: string
  new: number
  total: number
  change: number
}

/**
 * Fetch monthly trends
 *
 * GET /patients/bedridden/monthly-trends
 */
export function useBedriddenMonthlyTrends(params?: { district_id?: number; year?: number; months?: number }) {
  const queryParams = new URLSearchParams()
  if (params?.district_id) {
    queryParams.append('district_id', params.district_id.toString())
  }
  if (params?.year) {
    queryParams.append('year', params.year.toString())
  }
  if (params?.months) {
    queryParams.append('months', params.months.toString())
  }

  const url = `/patients/bedridden/monthly-trends${queryParams.toString() ? `?${queryParams.toString()}` : ''}`

  const { data, error, isLoading } = useSWR<MonthlyTrend[]>(url, apiClient.fetcher, {
    revalidateOnFocus: false,
    dedupingInterval: 30000, // 30 seconds
  })

  return {
    data: data || [],
    isLoading,
    error,
  }
}

/**
 * Patient List Response Type
 */
export interface BedriddenPatient {
  id: number
  district_id: number
  district_name: string
  subdistrict_id: number
  subdistrict_name: string
  vulnerable_group_ids: number[]
  type: 'home' | 'bed'
  severity: 'high' | 'medium' | 'low'
  chronic_disease_ids?: number[]
  chronic_disease_names?: string[]
  lat?: number
  lng?: number
  notes?: string
  created_at: string
  updated_at: string
}

interface BedriddenPatientListResponse {
  data: BedriddenPatient[]
  pagination: {
    page: number
    per_page: number
    total: number
    total_pages: number
  }
}

/**
 * Fetch paginated patient list
 *
 * GET /patients/bedridden
 */
export function useBedriddenPatientsList(params?: {
  page?: number
  per_page?: number
  district_id?: number
  type?: 'home' | 'bed'
  severity?: 'high' | 'medium' | 'low'
  search?: string
  vulnerable_group_id?: number
  disease_id?: number
}) {
  const queryParams = new URLSearchParams()
  if (params?.page) queryParams.append('page', params.page.toString())
  if (params?.per_page) queryParams.append('per_page', params.per_page.toString())
  if (params?.district_id) queryParams.append('district_id', params.district_id.toString())
  if (params?.type) queryParams.append('type', params.type)
  if (params?.severity) queryParams.append('severity', params.severity)
  if (params?.search) queryParams.append('search', params.search)
  if (params?.vulnerable_group_id) queryParams.append('vulnerable_group_id', params.vulnerable_group_id.toString())
  if (params?.disease_id) queryParams.append('disease_id', params.disease_id.toString())

  const url = `/patients/bedridden${queryParams.toString() ? `?${queryParams.toString()}` : ''}`

  const { data, error, isLoading, mutate } = useSWR<{ data: BedriddenPatient[]; pagination?: any }>(
    url,
    apiClient.paginatedFetcher,
    {
      revalidateOnFocus: false,
      dedupingInterval: 5000,
    }
  )

  return {
    patients: data?.data || [],
    pagination: data?.pagination,
    isLoading,
    error,
    mutate,
  }
}

/**
 * Fetch single patient by ID
 *
 * GET /patients/bedridden/{id}
 */
export function useBedriddenPatient(id: number | null) {
  const url = id ? `/patients/bedridden/${id}` : null

  const { data, error, isLoading, mutate } = useSWR<BedriddenPatient>(url, apiClient.fetcher, {
    revalidateOnFocus: false,
  })

  return {
    patient: data,
    isLoading,
    error,
    mutate,
  }
}

/**
 * District Type
 */
export interface District {
  id: number
  province_id: number
  name_th: string
  name_en: string
  code: string
  latitude?: number
  longitude?: number
  population?: number
}

/**
 * Fetch all districts
 *
 * GET /districts
 */
export function useDistricts() {
  const { data, error, isLoading } = useSWR<District[]>('/districts', apiClient.fetcher, {
    revalidateOnFocus: false,
    dedupingInterval: 60000, // Cache for 1 minute
  })

  return {
    districts: data || [],
    isLoading,
    error,
  }
}

/**
 * Subdistrict Type
 */
export interface Subdistrict {
  id: number
  district_id: number
  name_th: string
  name_en: string
  code: string
  population?: number
}

/**
 * Fetch subdistricts by district ID
 *
 * GET /subdistricts?district_id={id}
 */
export function useSubdistricts(districtId: number | null) {
  const url = districtId ? `/subdistricts?district_id=${districtId}` : null

  const { data, error, isLoading } = useSWR<Subdistrict[]>(url, apiClient.fetcher, {
    revalidateOnFocus: false,
    dedupingInterval: 60000,
  })

  return {
    subdistricts: data || [],
    isLoading,
    error,
  }
}

/**
 * Disease Type
 */
export interface Disease {
  id: number
  name_th: string
  name_en: string
  icd10_code?: string
  category?: string
}

/**
 * Fetch all diseases
 *
 * GET /diseases
 */
export function useDiseases() {
  const { data, error, isLoading } = useSWR<Disease[]>('/diseases', apiClient.fetcher, {
    revalidateOnFocus: false,
    dedupingInterval: 60000,
  })

  return {
    diseases: data || [],
    isLoading,
    error,
  }
}

/**
 * Create Patient Data Type
 */
export interface CreateBedriddenPatientData {
  district_id: number
  subdistrict_id: number
  vulnerable_group_ids: number[]
  type: 'home' | 'bed'
  severity: 'high' | 'medium' | 'low'
  chronic_disease_ids?: number[]
  lat?: number
  lng?: number
  notes?: string
}

/**
 * Create new patient
 *
 * POST /patients/bedridden
 */
export async function createBedriddenPatient(data: CreateBedriddenPatientData): Promise<BedriddenPatient> {
  const response = await apiClient.post<BedriddenPatient>('/patients/bedridden', data)
  return response.data
}

/**
 * Update existing patient
 *
 * PUT /patients/bedridden/{id}
 */
export async function updateBedriddenPatient(
  id: number,
  data: Partial<CreateBedriddenPatientData>
): Promise<BedriddenPatient> {
  const response = await apiClient.put<BedriddenPatient>(`/patients/bedridden/${id}`, data)
  return response.data
}

/**
 * Delete patient (soft delete)
 *
 * DELETE /patients/bedridden/{id}
 */
export async function deleteBedriddenPatient(id: number): Promise<void> {
  await apiClient.delete(`/patients/bedridden/${id}`)
  // Delete returns void, no data to return
}
